// Estado do espaço de trabalho no cliente: a árvore de páginas inteira em
// memória (e em cache local), páginas abertas em cache, criação otimista com
// ids gerados aqui e um feed de mudanças que mantém tudo sincronizado.

import { api, ApiError } from "./api.js";
import { safeGet, safeSet, toast } from "./dom.js";

export const state = {
  me: null,
  users: [],
  workspaces: [], // ambientes que a pessoa vê: {id, name, icon, description, role, members}
  favorites: [], // ids das páginas favoritas, na ordem
  unread: 0, // notificações não lidas
  lastNotification: 0,
  access: "",
  features: {}, // recursos do servidor (ex.: live = edição simultânea)
  tree: new Map(), // id -> {id, parent_id, kind, title, icon, position, owner_id, workspace_id, updated_at, shared_role}
  since: 0,
  ready: false,
};

const pages = new Map(); // id -> {data: resposta de GET /pages/:id, at}
// Campos alterados aqui e ainda não confirmados pelo servidor: uma leitura
// que chegue antes não pode desfazer o que o usuário acabou de digitar.
const unsaved = new Map(); // id -> {fields: {...}, count}
const LOCAL_FIELDS = ["title", "icon", "position", "parent_id", "content", "schema"];

function overlayUnsaved(id, page, treeNode) {
  const u = unsaved.get(id);
  if (!u) return;
  for (const [k, v] of Object.entries(u.fields)) {
    if (k === "props") { if (page) page.props = { ...page.props, ...v }; continue; }
    if (page) page[k] = v;
    if (treeNode && k in treeNode) treeNode[k] = v;
  }
}
const inflight = new Map(); // id -> Promise da criação (POST ainda não respondeu)
const listeners = new Map();

export function on(event, fn) {
  if (!listeners.has(event)) listeners.set(event, new Set());
  listeners.get(event).add(fn);
  return () => listeners.get(event).delete(fn);
}

export function emit(event, payload) {
  for (const fn of listeners.get(event) || []) {
    try { fn(payload); } catch (err) { console.error(err); }
  }
}

// ---------- Saving indicator ----------

let saving = 0;
function track(promise) {
  saving++;
  emit("saving", saving);
  return promise.finally(() => {
    saving--;
    emit("saving", saving);
  });
}
export const isSaving = () => saving > 0;

// Repete o envio quando a conexão falha (não quando o servidor recusa).
export async function withRetry(fn, delays = [2000, 5000, 5000, 10000]) {
  for (let i = 0; ; i++) {
    try {
      return await fn();
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 0 || i >= delays.length) throw err;
      emit("offline", true);
      await new Promise((r) => setTimeout(r, delays[i]));
    }
  }
}

// Toda operação numa página espera a criação dela terminar no servidor.
async function ready(id) {
  const p = inflight.get(id);
  if (p) await p.catch(() => {});
}

// ---------- Árvore ----------

const cacheKey = () => `ordo.tree.${state.me?.id}`;

export async function init(me) {
  state.me = me;
  state.tree.clear();
  pages.clear();
  const cached = safeGet(cacheKey());
  if (cached?.pages) {
    for (const p of cached.pages) state.tree.set(p.id, p);
    state.users = cached.users || [];
    state.workspaces = cached.workspaces || [];
    state.favorites = cached.favorites || [];
    state.features = cached.features || {};
    state.ready = true;
    emit("tree");
  }
  await loadTree();
  startPolling();
}

export async function loadTree() {
  const res = await api("GET", "/workspace");
  state.tree.clear();
  for (const p of res.pages) state.tree.set(p.id, p);
  // Páginas criadas aqui e ainda não confirmadas continuam na árvore.
  for (const id of inflight.keys()) {
    const p = pages.get(id)?.data?.page;
    if (p && p.kind !== "row") state.tree.set(id, treeEntry(p));
  }
  state.users = res.users;
  state.workspaces = res.workspaces || [];
  state.favorites = res.favorites || [];
  state.access = res.access || "";
  state.features = res.features || {};
  if (res.me) state.me = { ...state.me, ...res.me };
  state.since = res.now;
  state.ready = true;
  saveTreeCache();
  emit("tree");
}

const saveTreeCache = (() => {
  let t = null;
  return () => {
    clearTimeout(t);
    t = setTimeout(() => safeSet(cacheKey(), {
      pages: [...state.tree.values()], users: state.users, workspaces: state.workspaces, favorites: state.favorites,
      features: state.features,
    }), 500);
  };
})();

function treeEntry(p) {
  return {
    id: p.id, parent_id: p.parent_id || null, kind: p.kind, title: p.title || "", icon: p.icon || null,
    position: p.position, owner_id: p.owner_id || state.me.id, updated_at: p.updated_at || Date.now(),
    workspace_id: p.workspace_id || null, shared_role: p.shared_role, owner_name: p.owner_name,
  };
}

export const node = (id) => state.tree.get(id);

export function children(parentId) {
  const out = [];
  for (const p of state.tree.values()) if ((p.parent_id || null) === (parentId || null)) out.push(p);
  return out.sort((a, b) => a.position - b.position || String(a.title).localeCompare(String(b.title)));
}

// Raízes visíveis: as do topo de cada ambiente, as particulares do usuário e
// as compartilhadas com ele (cujo pai ele não enxerga).
export function roots() {
  const mine = [];
  const shared = [];
  const spaces = new Map(state.workspaces.map((w) => [w.id, []]));
  for (const p of state.tree.values()) {
    if (p.parent_id && state.tree.has(p.parent_id)) continue;
    if (!p.parent_id && p.workspace_id && spaces.has(p.workspace_id)) spaces.get(p.workspace_id).push(p);
    else if (p.owner_id === state.me.id && !p.parent_id && !p.workspace_id) mine.push(p);
    else shared.push(p);
  }
  const sort = (a, b) => a.position - b.position || String(a.title).localeCompare(String(b.title));
  for (const list of spaces.values()) list.sort(sort);
  return { mine: mine.sort(sort), shared: shared.sort(sort), spaces };
}

export const workspace = (id) => state.workspaces.find((w) => w.id === id) || null;

// Ambiente de uma página (pela própria página ou pela de cima).
export function workspaceOf(id) {
  const p = state.tree.get(id) || pages.get(id)?.data?.page;
  if (p?.workspace_id) return workspace(p.workspace_id);
  return null;
}

// Pode criar páginas no ambiente?
export const canEditWorkspace = (w) => !!w && (w.role === "edit" || w.role === "admin");

// Pode editar a página? (pela árvore local: ambiente, compartilhamento ou dono)
export function canEdit(p) {
  if (!p || !state.me) return false;
  if (state.me.role === "admin") return true;
  let cur = p;
  let guard = 0;
  while (cur && guard++ < 60) {
    if (cur.shared_role) return cur.shared_role !== "view";
    if (cur.workspace_id && !cur.parent_id) return canEditWorkspace(workspace(cur.workspace_id));
    if (!cur.parent_id) return cur.owner_id === state.me.id;
    cur = state.tree.get(cur.parent_id);
  }
  if (p.workspace_id) return canEditWorkspace(workspace(p.workspace_id));
  return p.owner_id === state.me.id;
}

export async function reloadWorkspaces() {
  const res = await api("GET", "/workspaces");
  state.workspaces = res.workspaces;
  saveTreeCache();
  emit("tree");
  return res.workspaces;
}

// ---------- Favoritos ----------

export const isFavorite = (id) => state.favorites.includes(id);

export async function toggleFavorite(id) {
  const on = !isFavorite(id);
  state.favorites = on ? [...state.favorites, id] : state.favorites.filter((x) => x !== id);
  emit("tree");
  try {
    await api(on ? "POST" : "DELETE", on ? "/favorites" : `/favorites/${id}`, on ? { page_id: id } : undefined);
  } catch (err) {
    toast("Não foi possível atualizar os favoritos: " + err.message);
    state.favorites = on ? state.favorites.filter((x) => x !== id) : [...state.favorites, id];
    emit("tree");
  }
  saveTreeCache();
  return on;
}

// Caminho até a página (do topo para baixo), usando a árvore local.
export function ancestors(id) {
  const out = [];
  let cur = state.tree.get(id) || pages.get(id)?.data?.page;
  let guard = 0;
  while (cur?.parent_id && guard++ < 50) {
    const parent = state.tree.get(cur.parent_id) || pages.get(cur.parent_id)?.data?.page;
    if (!parent) break;
    out.unshift(parent);
    cur = parent;
  }
  return out;
}

export function isInside(id, ancestorId) {
  return ancestors(id).some((a) => a.id === ancestorId);
}

// Gravar e transcrever chamadas é só para administradores do Ordo.
export const canTranscribe = () => state.me?.role === "admin";

export function databases() {
  return [...state.tree.values()].filter((p) => p.kind === "database");
}

// ---------- Páginas ----------

export const cached = (id) => pages.get(id)?.data || null;

// Devolve a página do cache na hora (se houver) e atualiza em seguida.
export async function load(id, { fresh = false } = {}) {
  await ready(id);
  const hit = pages.get(id);
  if (hit && !fresh && Date.now() - hit.at < 2000) return hit.data;
  const data = await api("GET", `/pages/${id}`);
  overlayUnsaved(id, data.page);
  if (data.rows) for (const r of data.rows) overlayUnsaved(r.id, r);
  pages.set(id, { data, at: Date.now() });
  if (data.page.kind !== "row") {
    state.tree.set(id, { ...(state.tree.get(id) || {}), ...treeEntry(data.page), shared_role: state.tree.get(id)?.shared_role });
  }
  return data;
}

export function setCached(id, data) {
  pages.set(id, { data, at: Date.now() });
}

export function invalidate(id) {
  const hit = pages.get(id);
  if (hit) hit.at = 0;
}

// Cria uma página na hora (id gerado aqui) e envia ao servidor em segundo plano.
export function create(body = {}) {
  const id = crypto.randomUUID();
  const parent = body.parent_id ? (state.tree.get(body.parent_id) || pages.get(body.parent_id)?.data?.page) : null;
  let kind = body.kind || "page";
  if (parent?.kind === "database") kind = "row";
  const now = Date.now();
  const siblings = kind === "row" ? [] : children(body.parent_id);
  const position = body.position ?? (siblings.length ? siblings.at(-1).position + 1000 : now);
  const page = {
    id, parent_id: body.parent_id || null, kind, title: body.title || "", icon: body.icon || null,
    content: body.content || null, props: body.props || {}, schema: body.schema || null,
    position, owner_id: state.me.id, created_at: now, updated_at: now, updated_by: state.me.id,
    workspace_id: (parent ? parent.workspace_id : body.workspace_id) || null,
  };
  if (kind === "database" && !page.schema) page.schema = defaultSchema();
  const optimistic = {
    page, role: "owner", children: [], breadcrumb: [],
    rows: kind === "database" ? [] : undefined,
    users: state.users, activity: [], shares: [],
    database: kind === "row" ? parentData(body.parent_id)?.page : undefined,
  };
  pages.set(id, { data: optimistic, at: now });
  if (kind !== "row") state.tree.set(id, treeEntry(page));
  else {
    const db = parentData(body.parent_id);
    if (db?.rows) {
      db.rows.push({ ...page, computed: {} });
      emit("rows", body.parent_id);
    }
  }

  const promise = track(withRetry(() => api("POST", "/pages", { ...body, id, kind, position })))
    .then((res) => {
      const hit = pages.get(id);
      if (hit) {
        hit.data.page = { ...res.page, title: hit.data.page.title, icon: hit.data.page.icon, content: hit.data.page.content ?? res.page.content };
        if (kind === "database") hit.data.page.schema = res.page.schema;
      }
      if (kind === "row") applyRow(body.parent_id, res.page);
      afterTouched(res.touched);
      return res.page;
    })
    .catch((err) => {
      pages.delete(id);
      state.tree.delete(id);
      emit("tree");
      emit("removed", id);
      toast("Não foi possível criar: " + err.message);
      throw err;
    })
    .finally(() => inflight.delete(id));
  inflight.set(id, promise);
  if (kind !== "row") {
    saveTreeCache();
    emit("tree");
  }
  return { id, page, promise };
}

function parentData(id) {
  return id ? pages.get(id)?.data : null;
}

// Atualiza uma página. Título, ícone, posição e pai mudam na árvore na hora.
export async function patch(id, body, { quiet = false } = {}) {
  const t = state.tree.get(id);
  if (t) {
    for (const k of ["title", "icon", "position", "parent_id", "workspace_id"]) if (k in body) t[k] = body[k];
    t.updated_at = Date.now();
    saveTreeCache();
    emit("tree");
  }
  const hit = pages.get(id);
  if (hit) {
    for (const k of ["title", "icon", "position", "parent_id", "content", "schema"]) if (k in body) hit.data.page[k] = body[k];
    if (body.props) hit.data.page.props = { ...hit.data.page.props, ...body.props };
  }
  const u = unsaved.get(id) || { fields: {}, count: 0 };
  for (const k of [...LOCAL_FIELDS, "props"]) {
    if (!(k in body)) continue;
    u.fields[k] = k === "props" ? { ...(u.fields.props || {}), ...body.props } : body[k];
  }
  u.count++;
  unsaved.set(id, u);
  const settle = () => { if (--u.count <= 0 && unsaved.get(id) === u) unsaved.delete(id); };
  await ready(id);
  const req = withRetry(() => api("PATCH", `/pages/${id}`, body)).finally(settle);
  try {
    const res = await (quiet ? req : track(req));
    const h = pages.get(id);
    if (h) {
      // Mantém o conteúdo local (o editor pode ter mudado depois do envio).
      const content = h.data.page.content;
      h.data.page = { ...h.data.page, ...res.page, content: "content" in body ? content : res.page.content };
      overlayUnsaved(id, h.data.page);
    }
    overlayUnsaved(id, res.page);
    if (res.page.kind === "row") applyRow(res.page.parent_id, res.page);
    afterTouched(res.touched);
    emit("page", res.page);
    return res;
  } catch (err) {
    toast(err.status === 0 ? "Sem conexão: a última alteração não foi salva." : "Não foi possível salvar: " + err.message);
    invalidate(id);
    throw err;
  }
}

// Atualiza a linha dentro do banco em cache (para o board/tabela refletirem).
function applyRow(dbId, row) {
  const db = pages.get(dbId)?.data;
  if (!db?.rows) return;
  const i = db.rows.findIndex((r) => r.id === row.id);
  const merged = { ...(i >= 0 ? db.rows[i] : {}), ...row };
  if (row.archived_at) {
    if (i >= 0) db.rows.splice(i, 1);
  } else if (i >= 0) db.rows[i] = merged;
  else db.rows.push(merged);
  emit("rows", dbId);
}

export function setRowLocal(dbId, rowId, changes) {
  const db = pages.get(dbId)?.data;
  const row = db?.rows?.find((r) => r.id === rowId);
  if (row) {
    Object.assign(row, changes, changes.props ? { props: { ...row.props, ...changes.props } } : {});
    emit("rows", dbId);
  }
  const hit = pages.get(rowId);
  if (hit) {
    if (changes.props) hit.data.page.props = { ...hit.data.page.props, ...changes.props };
    if ("title" in changes) hit.data.page.title = changes.title;
  }
}

// Outras páginas mudaram por automação/sincronização: busca as mudanças já.
function afterTouched(touched) {
  if (!touched?.length) return;
  for (const id of touched) invalidate(id);
  pollSoon(150);
}

export async function archive(id) {
  const t = state.tree.get(id);
  const removed = [];
  // Some da árvore na hora (com tudo que está dentro).
  const drop = (pid) => {
    for (const c of children(pid)) drop(c.id);
    if (state.tree.delete(pid)) removed.push(pid);
  };
  if (t) drop(id);
  const hit = pages.get(id)?.data?.page;
  if (hit?.kind === "row") {
    const db = pages.get(hit.parent_id)?.data;
    if (db?.rows) {
      db.rows = db.rows.filter((r) => r.id !== id);
      emit("rows", hit.parent_id);
    }
  }
  emit("tree");
  emit("removed", id);
  await ready(id);
  try {
    await track(api("DELETE", `/pages/${id}`));
  } catch (err) {
    toast("Não foi possível excluir: " + err.message);
    await loadTree();
    throw err;
  }
  saveTreeCache();
}

export async function restore(id) {
  await track(api("POST", `/pages/${id}/restore`));
  await loadTree();
}

export async function destroy(id) {
  await track(api("DELETE", `/pages/${id}?permanent=1`));
}

// Apaga de vez tudo o que a pessoa pode apagar na lixeira -> {deleted, kept}.
export async function emptyTrash() {
  const res = await track(api("DELETE", "/trash"));
  await loadTree(); // favoritos de páginas apagadas somem
  return res;
}

export async function trash() {
  return (await api("GET", "/trash")).pages;
}

export async function search(q) {
  return (await api("GET", `/search?q=${encodeURIComponent(q)}`)).results;
}

// ---------- Feed de mudanças ----------

let pollTimer = null;
let polling = false;
let notifPrimed = false;

function startPolling() {
  clearTimeout(pollTimer);
  pollSoon(document.hidden ? 15000 : 2500);
}

export function pollSoon(ms = 0) {
  clearTimeout(pollTimer);
  pollTimer = setTimeout(poll, ms);
}

export function stopPolling() {
  clearTimeout(pollTimer);
  pollTimer = null;
}

document.addEventListener("visibilitychange", () => {
  if (state.me && !document.hidden) pollSoon(0);
});

async function poll() {
  if (!state.me || polling) return;
  polling = true;
  try {
    const res = await api("GET", `/changes?since=${state.since}`, undefined, { timeout: 15000 });
    state.since = res.now;
    emit("offline", false);
    applyChanges(res.pages);
    // Entrou ou saiu de um ambiente, ou mudou de papel: recarrega tudo.
    if (res.access !== undefined && state.access && res.access !== state.access) await loadTree();
    if (res.unread !== undefined) {
      const fresh = notifPrimed && res.lastNotification > state.lastNotification;
      const changed = !notifPrimed || res.unread !== state.unread || res.lastNotification !== state.lastNotification;
      state.unread = res.unread;
      state.lastNotification = res.lastNotification;
      notifPrimed = true;
      if (changed) emit("notifications", { unread: res.unread, fresh });
    }
  } catch (err) {
    if (err.fatal) {
      emit("signed-out");
      polling = false;
      return;
    }
  }
  polling = false;
  pollTimer = setTimeout(poll, document.hidden ? 15000 : 2500);
}

function applyChanges(changed) {
  if (!changed.length) return;
  let treeChanged = false;
  const dbs = new Set();
  const external = [];
  for (const p of changed) {
    if (inflight.has(p.id)) continue;
    if (p.kind === "row") overlayUnsaved(p.id, p);
    if (p.kind !== "row") {
      if (p.archived_at) treeChanged = state.tree.delete(p.id) || treeChanged;
      else {
        const prev = state.tree.get(p.id);
        const entry = { ...treeEntry(p), shared_role: prev?.shared_role, owner_name: prev?.owner_name };
        overlayUnsaved(p.id, null, entry);
        state.tree.set(p.id, entry);
        treeChanged = true;
      }
    } else {
      const db = pages.get(p.parent_id)?.data;
      if (db?.rows) {
        const i = db.rows.findIndex((r) => r.id === p.id);
        if (p.archived_at) { if (i >= 0) db.rows.splice(i, 1); }
        else if (i >= 0) Object.assign(db.rows[i], { title: p.title, icon: p.icon, props: p.props, position: p.position, updated_at: p.updated_at });
        else db.rows.push({ ...p });
        dbs.add(p.parent_id);
      }
    }
    const hit = pages.get(p.id);
    if (hit) {
      hit.at = 0;
      if (p.kind === "row" && p.props) Object.assign(hit.data.page, { props: p.props, title: p.title });
    }
    if (p.updated_by && p.updated_by !== state.me.id) external.push(p);
    if (p.archived_at) emit("removed", p.id);
  }
  if (treeChanged) {
    saveTreeCache();
    emit("tree");
  }
  for (const id of dbs) emit("rows", id);
  // Propriedades calculadas (rollup/lookup) de outros bancos dependem destas linhas.
  const stale = new Set(changed.filter((p) => p.kind === "row").map((p) => p.parent_id));
  for (const id of stale) emit("rows-stale", id);
  emit("changes", { pages: changed, external, dbs: [...dbs] });
}

export function defaultSchema() {
  return {
    properties: [
      { id: "status", name: "Status", type: "select", options: [
        { name: "A fazer", color: "gray" }, { name: "Fazendo", color: "blue" }, { name: "Feito", color: "green" }] },
      { id: "resp", name: "Responsável", type: "person" },
      { id: "prazo", name: "Prazo", type: "date" },
    ],
    views: [
      { id: "board", name: "Board", type: "board", groupBy: "status" },
      { id: "table", name: "Tabela", type: "table" },
    ],
    automations: [],
  };
}

// Páginas visitadas recentemente (para a busca rápida e a tela inicial).
export function visited(id) {
  const list = safeGet("ordo.recent", []).filter((x) => x !== id);
  list.unshift(id);
  safeSet("ordo.recent", list.slice(0, 30));
}

export function recent() {
  return safeGet("ordo.recent", []).map((id) => state.tree.get(id)).filter(Boolean);
}

export function signOut() {
  stopPolling();
  notifPrimed = false;
  state.unread = 0;
  state.lastNotification = 0;
  state.me = null;
  state.tree.clear();
  pages.clear();
  state.ready = false;
}
